'use client'

import React, { useEffect, useRef, useState } from 'react'
import StoreBadges from '@/app/components/StoreBadges'
import { track } from '@/utils/analytics'

/**
 * Inline "commit, then reveal" widget for blog articles.
 *
 * ── Why this shape, from the 2026-08-03 funnel audit ───────────────────────
 *
 * The site has two genuinely sticky interactive tools and neither of them gets
 * used, because both live on their own page:
 *   • /risk-checker      — 88.9% completion once started (24 of 27), but only
 *                          27 starts in 28 days.
 *   • ingredient checker — 7.3 scans per user, the highest-CTR page on the
 *                          site in GSC (7.93%), but only 39 users.
 *
 * The reason is not that the tools are bad. It is that GA4 measures
 * 1.12 pages per session: readers land from search, read one page, and leave.
 * They will not click through to a tool, no matter how good it is. Any
 * interaction we want has to happen inside the article they already landed on.
 *
 * So this widget takes nothing but a tap, renders inline, and never navigates.
 *
 * ── Why "commit, then reveal" rather than a fact box ───────────────────────
 *
 * A static fun-fact box is passive: the eye slides over it. Asking the reader
 * to *commit to a guess first* does three things a fact box cannot:
 *
 *  1. It opens a curiosity gap the reader now has a stake in closing, which is
 *     what carries them past the scroll cliff. Only 37.5% of pageviews reach
 *     scroll_25 today, so anything that buys continued scrolling is worth more
 *     than anything placed further down.
 *  2. The reveal lands as a small surprise, and the install ask rides that
 *     moment instead of arriving cold at the foot of the page, where only 7.1%
 *     of readers ever get to.
 *  3. The guess itself is a measurement. `guess_reveal_answer` tells us what
 *     readers believe before we correct them, which is genuinely useful for
 *     picking future article angles.
 *
 * Published benchmarks for interactive vs static content (2–3x engagement,
 * ~52% more time on page) point the same way, but the local numbers above are
 * the actual argument.
 *
 * ── Placement ──────────────────────────────────────────────────────────────
 *
 * Drop it high — directly after the key-takeaways block, near the auto-injected
 * MidArticleCTA. Reach is everything: mid_article_cta_impression (fires on
 * intersection) is 838 against hero_answer_impression (fires on mount) of
 * 1,743, i.e. only 48% of readers get that far. Anything below the halfway mark
 * is invisible to most of the audience.
 *
 * ── Honesty constraint ─────────────────────────────────────────────────────
 *
 * The reveal must be a real figure from the article, with its real source.
 * This is a YMYL site and the widget is not permitted to dramatise a number the
 * body copy does not support — `answerNote` should say where it comes from.
 */
export interface GuessOption {
  /** Short label shown on the button, e.g. "About 10". */
  label: string
  /** True for the single correct option. */
  correct?: boolean
}

export interface InlineGuessRevealProps {
  /** The question. Keep it to one line and make it concrete and numeric. */
  question: string
  /** 2–4 options. Exactly one should have `correct: true`. */
  options: GuessOption[]
  /** The headline answer, e.g. "35 of 50". Kept short — it is the payoff. */
  answer: string
  /** One or two sentences explaining the answer. Must match the article. */
  answerNote: React.ReactNode
  /** Stable GA4 label, e.g. `crest_polymer_count`. */
  source: string
  /** Contextual install ask shown under the reveal. */
  ctaText?: string
  /** Button label for the install ask. */
  ctaLabel?: string
}

export default function InlineGuessReveal({
  question,
  options,
  answer,
  answerNote,
  source,
  ctaText = 'Most people get this wrong because the label never tells you. The app reads it for you.',
}: InlineGuessRevealProps) {
  const [picked, setPicked] = useState<number | null>(null)
  const ref = useRef<HTMLElement | null>(null)
  const seen = useRef(false)

  // Impression tracking mirrors MidArticleCTA: IntersectionObserver so the
  // count means "actually scrolled into view", not "rendered". That keeps it
  // directly comparable with mid_article_cta_impression when we measure reach.
  useEffect(() => {
    const el = ref.current
    if (!el || seen.current) return

    const fire = () => {
      if (seen.current) return
      seen.current = true
      track('guess_reveal_view', { source, page_type: 'blog' })
    }

    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            fire()
            observer.disconnect()
          }
        })
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [source])

  const correctIndex = options.findIndex((o) => o.correct)

  const choose = (i: number) => {
    if (picked !== null) return
    setPicked(i)
    const wasRight = i === correctIndex
    track('guess_reveal_answer', {
      source,
      page_type: 'blog',
      guess: options[i]?.label ?? String(i),
      guess_index: i,
      was_correct: wasRight,
    })
    track('guess_reveal_result', {
      source,
      page_type: 'blog',
      was_correct: wasRight,
    })
  }

  const revealed = picked !== null
  const wasCorrect = revealed && picked === correctIndex

  return (
    <aside
      ref={ref}
      className="not-prose my-8 rounded-xl border border-[#1E6B47]/25 bg-[#1E6B47]/[0.06] p-5"
      aria-label="Quick check"
    >
      <p className="text-2xs font-semibold uppercase tracking-wider text-[#1E6B47]">
        Quick check
      </p>
      <p className="mt-1.5 font-display text-lg font-bold leading-snug text-ink">
        {question}
      </p>

      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={question}>
        {options.map((o, i) => {
          const isCorrect = i === correctIndex
          const isPicked = picked === i
          let cls =
            'rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors '
          if (!revealed) {
            cls +=
              'border-[#1E6B47]/30 bg-white text-gray-800 hover:border-[#1E6B47] hover:bg-[#1E6B47]/10 cursor-pointer'
          } else if (isCorrect) {
            cls += 'border-[#1E6B47] bg-[#1E6B47] text-white'
          } else if (isPicked) {
            cls += 'border-gray-300 bg-gray-100 text-gray-500 line-through'
          } else {
            cls += 'border-gray-200 bg-white text-gray-400'
          }
          return (
            <button
              key={o.label}
              type="button"
              onClick={() => choose(i)}
              disabled={revealed}
              aria-pressed={isPicked}
              className={cls}
            >
              {o.label}
              {revealed && isCorrect && <span aria-hidden="true"> ✓</span>}
            </button>
          )
        })}
      </div>

      {revealed && (
        <div className="mt-4 border-t border-[#1E6B47]/20 pt-4">
          <p className="text-sm font-semibold text-[#1E6B47]">
            {wasCorrect ? 'Correct.' : 'Not quite.'}{' '}
            <span className="text-ink">The answer is {answer}.</span>
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-gray-700">{answerNote}</p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <StoreBadges
              source={`guess_reveal_${source}`}
              ctaPosition="mid_article"
              trackingParams={{ page_type: 'blog', was_correct: wasCorrect }}
              size="sm"
            />
            <span className="text-xs leading-snug text-gray-600">{ctaText}</span>
          </div>
        </div>
      )}
    </aside>
  )
}
